import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { MessageCircle, Share2, Loader2, ShieldAlert, ChevronRight, RotateCcw, ScanLine } from "lucide-react";
import { toast } from "sonner";
import { Shell, TopBar } from "../components/Shell";
import { PRIORITY, DISCLAIMER } from "../lib/data";
import { api } from "../lib/api";

const FINDING_LABELS = {
  possible_caries: "Possible cavity-like area",
  missing_tooth: "Missing tooth",
  visible_fracture: "Visible fracture / chipping",
  visible_wear: "Visible tooth wear",
  visible_discoloration: "Discoloration",
  visible_restoration: "Existing filling / restoration",
  visible_erosion: "Enamel erosion / abrasion",
  crowding: "Crowding / misalignment",
  gingival_inflammation: "Gum inflammation",
  recession: "Gum recession",
  visible_plaque: "Visible plaque",
  visible_calculus: "Visible tartar (calculus)",
  orthodontic_appliance: "Orthodontic appliance",
  no_visible_findings: "No visible concerns detected",
  other: "Other finding",
};
const fLabel = (f) => FINDING_LABELS[f] || (f || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
const OVERALL_TEXT = {
  urgent: "Professional dental care recommended soon",
  attention: "Professional dental assessment recommended",
  consider: "Preventive / consideration items",
};
const GROUP_ORDER = ["urgent", "attention", "consider"];

export default function Report() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const sid = params.get("sid");
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [xlang, setXlang] = useState("en");

  useEffect(() => {
    if (!sid) return navigate("/intake");
    api.get(`/screenings/${sid}/report`)
      .then((r) => setReport(r.data))
      .catch(() => toast.error("Report not ready."))
      .finally(() => setLoading(false));
  }, [sid]);

  if (loading) {
    return (
      <Shell header={<TopBar title="Your screening" onBack={() => navigate("/")} />}>
        <div className="flex-1 flex items-center justify-center"><Loader2 className="w-7 h-7 text-primary animate-spin" /></div>
      </Shell>
    );
  }
  if (!report) {
    return (
      <Shell header={<TopBar title="Your screening" onBack={() => navigate("/")} />}>
        <div className="flex-1 flex flex-col items-center justify-center px-8 text-center gap-4">
          <div className="w-16 h-16 rounded-full bg-accent flex items-center justify-center">
            <ShieldAlert className="w-7 h-7 text-primary" />
          </div>
          <div>
            <h2 className="font-head font-bold text-xl tracking-tight">Report not ready</h2>
            <p className="text-sm text-muted-foreground mt-1 max-w-xs">We couldn't load your screening. You can run the analysis again.</p>
          </div>
          <button data-testid="report-retry" onClick={() => navigate(`/processing?sid=${sid}`)}
            className="tap h-12 px-6 rounded-2xl brand-gradient text-white font-semibold flex items-center gap-2">
            <RotateCcw className="w-4 h-4" /> Re-run analysis
          </button>
        </div>
      </Shell>
    );
  }

  const overall = report.overall || "consider";
  const op = PRIORITY[overall];
  const groups = {};
  (report.findings || []).forEach((f) => { (groups[f.priority] = groups[f.priority] || []).push(f); });

  return (
    <Shell header={<TopBar title="Your screening" onBack={() => navigate("/")} />}>
      <div className="px-6 py-5 flex flex-col gap-5 pb-40">
        {/* Overall banner */}
        <div data-testid="overall-banner" className="rounded-2xl p-5 text-white" style={{ background: op.color }}>
          <p className="text-[11px] font-bold uppercase tracking-wide opacity-90">Overall</p>
          <p className="font-head font-extrabold text-xl tracking-tight mt-0.5">{op.label}</p>
          <p className="text-sm opacity-90 mt-1">{OVERALL_TEXT[overall]}</p>
        </div>

        {/* Prominent amber disclaimer */}
        <div data-testid="report-disclaimer" className="flex gap-3 rounded-2xl border-l-4 border-[#E67300] bg-[#FDF6EC] p-4">
          <ShieldAlert className="w-5 h-5 text-[#E67300] shrink-0 mt-0.5" />
          <p className="text-[13px] leading-relaxed text-[#7A4A12]">
            This is <span className="font-bold">NOT a confirmed diagnosis</span>. Always consult a dentist before making any decision, and get a professional assessment for a final diagnosis.
          </p>
        </div>

        {/* Findings grouped */}
        {GROUP_ORDER.map((pri) => {
          const items = groups[pri];
          if (!items || !items.length) return null;
          const p = PRIORITY[pri];
          return (
            <div key={pri} className="flex flex-col gap-2.5" data-testid={`group-${pri}`}>
              <div className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-full" style={{ background: p.color }} />
                <h2 className="font-head font-bold text-base tracking-tight" style={{ color: p.text }}>{p.label}</h2>
                <span className="text-xs text-muted-foreground">({items.length})</span>
              </div>
              {items.map((f, i) => (
                <div key={i} data-testid={`finding-${pri}-${i}`} className="rounded-2xl border border-border bg-card overflow-hidden">
                  <div className="h-1" style={{ background: p.color }} />
                  <div className="p-4">
                    <div className="flex items-start justify-between gap-2">
                      <h3 className="font-head font-semibold text-[15px]">{fLabel(f.finding)}</h3>
                      {f.location && f.location !== "unspecified" && (
                        <span className="text-[11px] text-muted-foreground capitalize whitespace-nowrap">{String(f.location).replace(/_/g, " ")}</span>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground leading-relaxed mt-1">{f.evidence}</p>
                    <p className="text-[12px] italic text-muted-foreground mt-2">Professional confirmation required.</p>
                  </div>
                </div>
              ))}
            </div>
          );
        })}

        {/* IOPA / OPG X-ray analysis */}
        {report.xray?.images?.length > 0 && (
          <div className="flex flex-col gap-3" data-testid="report-xray">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <ScanLine className="w-4 h-4 text-primary" />
                <h2 className="font-head font-bold text-base tracking-tight">AI IOPA / OPG analysis</h2>
              </div>
              <div className="flex items-center gap-1 rounded-full bg-secondary p-0.5" data-testid="report-xray-lang">
                <button data-testid="report-xray-en" onClick={() => setXlang("en")}
                  className={`px-2.5 py-0.5 rounded-full text-[11px] font-semibold ${xlang === "en" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>EN</button>
                <button data-testid="report-xray-hi" onClick={() => setXlang("hi")}
                  className={`px-2.5 py-0.5 rounded-full text-[11px] font-semibold ${xlang === "hi" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>हिंदी</button>
              </div>
            </div>
            {report.xray.images.map((xi, idx) => {
              const issues = xi.issues || [];
              const qn = xlang === "hi" ? (xi.quality_note_hi || xi.quality_note) : xi.quality_note;
              const issueText = (i) => (xlang === "hi" ? (i.label_hi || i.label) : i.label);
              return (
                <div key={idx} data-testid={`report-xray-${idx}`} className="rounded-2xl border border-border bg-card overflow-hidden">
                  <div className="px-4 pt-3">
                    <span className="text-[13px] font-semibold">{xi.view_label}</span>
                  </div>
                  <img src={`data:image/jpeg;base64,${xi.annotated_image_base64}`} alt={`${xi.view_label} radiograph`} className="w-full mt-2" />
                  <div className="p-4">
                    <h3 className="text-[13px] font-semibold mb-2">{xlang === "hi" ? "निष्कर्ष" : "Findings"}</h3>
                    {issues.length === 0 ? (
                      <p className="text-sm text-muted-foreground">{xlang === "hi" ? "कोई विशेष समस्या नहीं मिली" : "No significant findings"}</p>
                    ) : (
                      <ul className="flex flex-wrap gap-2">
                        {issues.map((it, i) => (
                          <li key={i} className="text-[13px] font-semibold px-3 py-1.5 rounded-full bg-[#FCEBE8] text-[#9E2A16]">{issueText(it)}</li>
                        ))}
                      </ul>
                    )}
                    {qn && <p className="text-xs text-amber-700 mt-3 italic">{qn}</p>}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Limitations */}
        {report.limitations?.length > 0 && (
          <div className="rounded-2xl bg-secondary/60 border border-border p-4">
            <h3 className="font-head font-semibold text-sm mb-2">Limitations</h3>
            <ul className="text-[13px] text-muted-foreground list-disc pl-5 space-y-1">
              {report.limitations.map((l, i) => <li key={i}>{l}</li>)}
            </ul>
          </div>
        )}
      </div>

      {/* Footer actions */}
      <div className="sticky bottom-0 px-6 py-4 bg-background border-t border-border flex gap-3">
        <button data-testid="report-chat" onClick={() => navigate(`/chat?sid=${sid}`)}
          className="tap flex-1 h-13 py-3 rounded-2xl border border-border bg-card font-semibold flex items-center justify-center gap-2">
          <MessageCircle className="w-4 h-4" /> Ask DentaFriend
        </button>
        <button data-testid="report-share" onClick={() => navigate(`/control?sid=${sid}`)}
          className="tap flex-1 h-13 py-3 rounded-2xl brand-gradient text-white font-semibold flex items-center justify-center gap-2">
          <Share2 className="w-4 h-4" /> Share / Save
        </button>
      </div>
    </Shell>
  );
}
